import React from 'react'
import { Box, Button, Grid, IconButton, Stack, Typography } from '@mui/material'
import { UseFieldArrayRemove, useFormContext, useWatch } from 'react-hook-form'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useApp } from 'src/Hooks'
import { Field } from 'src/Libs/dynamic-tariff-settings/model'
import {
  DynamicTariffSettingsFormFields,
  ManualPowerPriceTranslationKey,
  MAX_MANUAL_POWER_PRICE_PERIODS,
} from 'src/Libs/dynamic-tariff-settings/constants'
import ManualPowerPriceRow from './ManualPowerPriceRow'
import { PowerPriceContainer } from './PowerPriceContainer'
import { J2Add } from '@e3dc-react/icons'
import { useFeatureFlagsContext } from 'src/Libs/feature-flags/FeatureFlagsContext'
import { isManualTariffTemporarilyUnavailable } from 'src/Libs/dynamic-tariff-settings/utils'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'

interface Props {
  fields: Field[]
  remove: UseFieldArrayRemove
  handleAddPeriod: () => void
}

/**
 * Form to manage manual power prices of dynamic tariff settings form.
 * Renders a flat list of weekday-based tariff periods.
 */
const ManualPowerPriceSettingsForm: React.FC<Props> = ({ fields, remove, handleAddPeriod }) => {
  const { t } = useCustomTranslation()
  const { isMobile } = useApp()
  const { formState } = useFormContext()
  const {
    featureFlags: { isManualTariffEnabled },
  } = useFeatureFlagsContext()
  const { hasInstallationWritingRights } = useInstallationAccessRightsContext()

  const powerPriceState = useWatch({ name: DynamicTariffSettingsFormFields.PowerPriceState })

  const hasReachedMaxPeriods = fields.length >= MAX_MANUAL_POWER_PRICE_PERIODS
  const powerPricesError = formState.errors.powerPrices as { message?: string; root?: { message?: string } } | undefined
  /* array-level (root) error, e.g. "at least 2 periods" or "incomplete coverage"; field errors render below their fields */
  const manualGeneralErrorMessage = powerPricesError?.root?.message ?? powerPricesError?.message
  const isManualTemporarilyUnavailable = isManualTariffTemporarilyUnavailable(powerPriceState, isManualTariffEnabled)

  const headers = (
    <Grid container>
      <Grid item xs={5}>
        <Typography variant="body1">{t(ManualPowerPriceTranslationKey.Weekdays)}</Typography>
      </Grid>
      <Grid item xs={2}>
        <Typography variant="body1">{t(ManualPowerPriceTranslationKey.From)}</Typography>
      </Grid>
      <Grid item xs={2}>
        <Typography variant="body1">{t(ManualPowerPriceTranslationKey.Until)}</Typography>
      </Grid>
      <Grid item xs={3}>
        <Typography variant="body1">{t(ManualPowerPriceTranslationKey.Price)}</Typography>
      </Grid>
    </Grid>
  )

  const isButtonDisabled = !hasInstallationWritingRights || isManualTemporarilyUnavailable || hasReachedMaxPeriods

  return (
    <>
      {fields.length > 0 && (
        <PowerPriceContainer
          sx={theme => ({
            width: '100%',
            backgroundColor: theme.palette.grey.grey07,
            transition: theme.transitions.create('border'),
            flexDirection: 'column',
            ...(powerPricesError ? { border: `1px solid ${theme.palette.error.main}` } : {}),
          })}
        >
          <Box
            sx={{
              display: 'flex-column',
              width: '100%',
            }}
          >
            <Stack gap={2}>
              {!isMobile && headers}
              {fields.map((field, index) => (
                <ManualPowerPriceRow
                  key={field.id}
                  index={index}
                  onRemove={() => remove(index)}
                  hasWritingRights={hasInstallationWritingRights && !isManualTemporarilyUnavailable}
                  isRemoveDisabled={fields.length === 1}
                />
              ))}
            </Stack>
            {isMobile ? (
              <Button disabled={isButtonDisabled} sx={{ mt: 2, width: '100%' }} onClick={handleAddPeriod}>
                {t('dynamic-tariff-settings.add-date-frame')}
              </Button>
            ) : (
              <IconButton disabled={isButtonDisabled} sx={{ color: 'grey.grey00' }} onClick={handleAddPeriod}>
                <J2Add />
              </IconButton>
            )}
          </Box>
          {isManualTemporarilyUnavailable && (
            <Typography variant="body2" sx={{ color: 'warning.main', mr: 'auto' }}>
              {t('dynamic-tariff-settings.manual-power-price.temporarily-unavailable')}
            </Typography>
          )}
        </PowerPriceContainer>
      )}
      <Stack sx={{ mb: 4 }} direction={isMobile ? 'column' : 'row'} justifyContent="space-between" alignItems="center">
        {manualGeneralErrorMessage && (
          <Typography sx={{ color: 'error.main' }} variant="body2">
            {manualGeneralErrorMessage}
          </Typography>
        )}
      </Stack>
    </>
  )
}

export default ManualPowerPriceSettingsForm
